"use client";

import { useForm } from "react-hook-form";
import type { Resolver, FieldErrors } from "react-hook-form";
import * as z from "zod";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useReCaptcha } from "next-recaptcha-v3";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { sendContactEmail } from "@/actions/email";
import { useState } from "react";

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

type FormData = {
	name: string;
	email: string;
	subject: string;
	message: string;
};

function ContactFormBase({
	getToken,
}: {
	getToken: (() => Promise<string>) | null;
}) {
	const t = useTranslations("ContactForm");

	const schema = useMemo(
		() =>
			z.object({
				name: z.string().min(2, { message: t("nameMin") }),
				email: z.string().email({ message: t("invalidEmail") }),
				subject: z.string().min(5, { message: t("subjectMin") }),
				message: z.string().min(10, { message: t("messageMin") }),
			}),
		[t],
	);

	const customZodResolver: Resolver<FormData> = useMemo(
		() => (values) => {
			const result = schema.safeParse(values);
			if (result.success) {
				return { values: result.data, errors: {} };
			}
			const errors: FieldErrors<FormData> = {};
			for (const issue of result.error.issues) {
				const path = issue.path[0] as keyof FormData;
				if (path && !errors[path]) {
					errors[path] = { type: "validation", message: issue.message };
				}
			}
			return { values: {} as Record<string, never>, errors };
		},
		[schema],
	);

	const [status, setStatus] = useState<
		"idle" | "submitting" | "success" | "error"
	>("idle");
	const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset,
	} = useForm<FormData>({
		resolver: customZodResolver,
		mode: "onChange",
	});

	const onSubmit = async (data: FormData) => {
		setStatus("submitting");
		setRecaptchaError(null);
		let token: string | null = null;
		if (getToken) {
			try {
				token = await getToken();
			} catch {
				setRecaptchaError(t("recaptchaError"));
				setStatus("idle");
				return;
			}
		}
		const formData = new FormData();
		formData.append("name", data.name);
		formData.append("email", data.email);
		formData.append("subject", data.subject);
		formData.append("message", data.message);
		if (token) formData.append("recaptchaToken", token);

		const result = await sendContactEmail(formData);
		if (result.success) {
			setStatus("success");
			reset();
		} else {
			setStatus("error");
		}
	};

	const inputClass = "h-16 text-xl px-6 py-5 placeholder:text-gray-500";
	const errorClass = "text-red-500 text-xl mt-1";

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
			<div>
				<Input
					{...register("name")}
					placeholder={t("namePlaceholder")}
					className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
				/>
				{errors.name && <p className={errorClass}>{errors.name.message}</p>}
			</div>
			<div>
				<Input
					{...register("email")}
					placeholder={t("emailPlaceholder")}
					type="email"
					className={`${inputClass} ${errors.email ? "border-red-500" : ""}`}
				/>
				{errors.email && <p className={errorClass}>{errors.email.message}</p>}
			</div>
			<div>
				<Input
					{...register("subject")}
					placeholder={t("subjectPlaceholder")}
					className={`${inputClass} ${errors.subject ? "border-red-500" : ""}`}
				/>
				{errors.subject && (
					<p className={errorClass}>{errors.subject.message}</p>
				)}
			</div>
			<div>
				<textarea
					{...register("message")}
					placeholder={t("messagePlaceholder")}
					className={`flex min-h-[220px] w-full rounded-md border border-gray-300 bg-white px-6 py-5 text-xl placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-700 dark:bg-white dark:text-gray-900 ${errors.message ? "border-red-500" : ""}`}
				/>
				{errors.message && (
					<p className={errorClass}>{errors.message.message}</p>
				)}
			</div>

			{recaptchaError && <p className={errorClass}>{recaptchaError}</p>}

			<Button
				type="submit"
				disabled={status === "submitting" || !isValid}
				loading={status === "submitting"}
				className="w-full min-h-[48px] h-14 sm:h-16 text-base sm:text-xl py-4 sm:py-6"
			>
				{status === "submitting" ? t("sending") : t("button")}
			</Button>

			{status === "success" && (
				<p className="text-green-600 text-center text-xl">{t("success")}</p>
			)}
			{status === "error" && (
				<p className="text-red-600 text-center text-xl">{t("error")}</p>
			)}
		</form>
	);
}

function ContactFormWithRecaptcha() {
	const { executeRecaptcha } = useReCaptcha();
	const getToken = () => executeRecaptcha("contact_submit");
	return <ContactFormBase getToken={getToken} />;
}

export default function ContactForm() {
	if (!recaptchaSiteKey) return <ContactFormBase getToken={null} />;
	return <ContactFormWithRecaptcha />;
}
